'use client';

import { useEffect } from 'react';
import { useFirestore } from '@/firebase';
import { collection, doc, getDocs, writeBatch, query, where, limit } from 'firebase/firestore';
import { moderateReview } from '@/ai/flows/moderate-review-flow';
import { type Review } from '@/models/review';

const MODERATION_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes instead of 24 hours
const LOCAL_STORAGE_KEY = 'lastModerationRun';
const LOCAL_STORAGE_STATS_KEY = 'moderationStats';
const WEEKLY_LIMIT = 50;
const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

interface ModerationStats {
  weekStart: number;
  count: number;
}

export default function AutomatedModerator() {
  const firestore = useFirestore();

  useEffect(() => {
    if (!firestore) return;

    const checkAndRunModeration = async () => {
      try {
        const lastRunString = localStorage.getItem(LOCAL_STORAGE_KEY);
        const lastRun = lastRunString ? parseInt(lastRunString, 10) : 0;
        const now = Date.now();

        // If we ran very recently, skip to avoid excessive AI calls
        if (now - lastRun < MODERATION_INTERVAL) {
          console.log(`[Moderator] Skipping check. Last run was ${Math.round((now - lastRun) / 1000)}s ago.`);
          return;
        }
        
        console.log('[Moderator] Checking for pending reviews...');
        
        // --- Weekly Rate Limiting Logic ---
        const statsString = localStorage.getItem(LOCAL_STORAGE_STATS_KEY);
        let stats: ModerationStats = statsString 
            ? JSON.parse(statsString) 
            : { weekStart: now, count: 0 };

        if (now - stats.weekStart > WEEK_IN_MS) {
            console.log('[Moderator] Resetting weekly moderation count.');
            stats = { weekStart: now, count: 0 };
        }

        if (stats.count >= WEEKLY_LIMIT) {
            console.warn(`[Moderator] Weekly limit of ${WEEKLY_LIMIT} reached. Moderation paused.`);
            return;
        }

        // Fetch pending reviews (limit to 10 at a time to stay within quotas/performance)
        const reviewsRef = collection(firestore, 'reviews');
        const q = query(reviewsRef, where('status', '==', 'pending'), limit(10));
        const querySnapshot = await getDocs(q);
        
        const pendingReviews = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Review));

        if (pendingReviews.length === 0) {
          console.log('[Moderator] No pending reviews found. Updating last run timestamp.');
          localStorage.setItem(LOCAL_STORAGE_KEY, now.toString());
          return;
        }

        const remainingQuota = WEEKLY_LIMIT - stats.count;
        const reviewsToProcess = pendingReviews.slice(0, remainingQuota);

        console.log(`[Moderator] Processing ${reviewsToProcess.length} pending reviews...`);
        const batch = writeBatch(firestore);
        let processedCount = 0;
        
        for (const review of reviewsToProcess) {
          try {
            console.log(`[Moderator] Moderating review from ${review.authorName}...`);
            const moderationResult = await moderateReview({
              text: review.text,
              authorName: review.authorName,
            });

            const reviewDocRef = doc(firestore, 'reviews', review.id);
            batch.update(reviewDocRef, { 
              status: moderationResult.status,
              moderationReason: moderationResult.reason,
            });
            processedCount++;

          } catch (aiError) {
            console.error(`[Moderator] AI moderation failed for ${review.id}:`, aiError);
          }
        }

        if (processedCount > 0) {
            await batch.commit();
            const newCount = stats.count + processedCount;
            localStorage.setItem(LOCAL_STORAGE_STATS_KEY, JSON.stringify({ ...stats, count: newCount }));
            localStorage.setItem(LOCAL_STORAGE_KEY, Date.now().toString());
            console.log(`[Moderator] Successfully processed ${processedCount} reviews. Total this week: ${newCount}/${WEEKLY_LIMIT}`);
        }

      } catch (error) {
        console.error('[Moderator] Error in automated moderation:', error);
      }
    };

    // Run check 2 seconds after mount
    const timeoutId = setTimeout(checkAndRunModeration, 2000); 

    return () => clearTimeout(timeoutId);
  }, [firestore]);

  return null; 
}
